import { useState } from "react";
import { supabase } from "@/services/supabase";
import { toast } from "sonner";

/**
 * Custom hook for creating Stripe one-time payment sessions
 * Supports both authenticated and guest checkout
 * @returns Object with loading state, error, and createPayment function
 */
export function useStripePayment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPayment = async (priceId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Call edge function to create payment session
      // No JWT required - supports guest checkout
      const { data, error: functionError } = await supabase.functions.invoke("create-payment", {
        body: { price_id: priceId },
      });

      if (functionError) {
        throw new Error(functionError.message);
      }

      if (!data || !data.url) {
        throw new Error("Failed to create payment session");
      }

      // Redirect to Stripe checkout
      // Using window.location.href instead of window.open to avoid mobile popup blockers
      window.location.href = data.url;
      
      toast.success("Redirecting to checkout...");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create payment";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, createPayment };
}
